import {
  Badge,
  Button,
  cn,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  ScrollArea,
} from "@devhop/ui";
import { Plus, ShoppingCart, Table, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useCart } from "../context/CartContext";
import { usePOSContext } from "../context/POSContext";
import { useCartAddPulse } from "../hooks/useCartAddPulse";
import { AddToOrderDialog } from "./AddToOrderDialog";
import { CartItem } from "./CartItem";
import { OrderSummaryDialog } from "./OrderSummaryDialog";
import { SalespersonSelector } from "./SalespersonSelector";
import { TableSelectionDialog } from "./TableSelectionDialog";

type CartContentProps = {
  showHeader?: boolean;
  showFooter?: boolean;
  onClear?: () => void;
};

export function CartContent({
  showHeader = true,
  showFooter = true,
  onClear,
}: CartContentProps) {
  const { items, total, clearCart } = useCart();
  const { selection, setSelection } = usePOSContext();
  const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
  const [isOrderSummaryDialogOpen, setIsOrderSummaryDialogOpen] =
    useState(false);
  const [isAddToOrderDialogOpen, setIsAddToOrderDialogOpen] = useState(false);

  const handleClear = () => {
    clearCart();
    onClear?.();
  };

  const handleClearTable = () => {
    if (!selection) return;
    const { tableId, tableName, ...rest } = selection;
    setSelection({
      ...rest,
    });
  };

  const itemCount = items.reduce((sum, item) => sum + (item.quantity ?? 1), 0);
  const isPulsing = useCartAddPulse();

  return (
    <>
      {showHeader && (
        <div className="space-y-3 border-b p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart
                data-cart-target
                className={cn(
                  "h-5 w-5 text-primary transition-transform",
                  isPulsing && "animate-cart-bounce",
                )}
              />
              <h2 className="font-heading font-semibold text-base">ກະຕ່າ</h2>
              {itemCount > 0 && (
                <Badge
                  variant="secondary"
                  className={cn(
                    "rounded-full transition-transform",
                    isPulsing && "animate-cart-bounce",
                  )}
                >
                  {itemCount}
                </Badge>
              )}
            </div>
            {items.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-destructive hover:text-destructive"
                onClick={handleClear}
                title="ລຶບທັງໝົດ"
              >
                <Trash2 className="mr-1 h-4 w-4" />
                ລຶບ
              </Button>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <SalespersonSelector branchId={selection?.branchId ?? null} />
            <div className="flex items-center gap-1">
              <Button
                variant={selection?.tableId ? "secondary" : "outline"}
                size="sm"
                onClick={() => setIsTableDialogOpen(true)}
                className="h-9"
              >
                <Table className="mr-2 h-4 w-4" />
                {selection?.tableName || "ເລືອກໂຕະ"}
              </Button>
              {selection?.tableId && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  onClick={handleClearTable}
                  title="ລຶບໂຕະ"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <TableSelectionDialog
        open={isTableDialogOpen}
        onOpenChange={setIsTableDialogOpen}
        branchId={selection?.branchId ?? null}
      />

      <ScrollArea className="min-h-0 flex-1">
        <div className="p-4">
          {items.length === 0 ? (
            <div className="flex h-full items-center justify-center py-12">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <ShoppingCart className="h-8 w-8 text-muted-foreground" />
                  </EmptyMedia>
                  <EmptyTitle>ກະຕ່າຫວ່າງ</EmptyTitle>
                  <EmptyDescription>ກະລຸນາເລືອກສິນຄ້າເພື່ອເພີ່ມໃສ່ກະຕ່າ</EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <CartItem key={item.id} item={item} />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      {showFooter && items.length > 0 && (
        <div className="space-y-3 border-t bg-muted/40 p-4">
          <div className="flex items-end justify-between">
            <span className="text-muted-foreground text-sm">ລາຄາລວມ</span>
            <span className="font-bold font-heading text-2xl text-primary tabular-nums">
              {total.toLocaleString("lo-LA")} ₭
            </span>
          </div>
          <div className="space-y-2">
            <Button
              className="h-12 w-full font-semibold text-base"
              size="lg"
              onClick={() => setIsOrderSummaryDialogOpen(true)}
            >
              ຢືນຢັນ Order
            </Button>
            <Button
              className="w-full"
              size="lg"
              variant="outline"
              onClick={() => setIsAddToOrderDialogOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              ເພີ່ມເຂົ້າ Order
            </Button>
          </div>
        </div>
      )}

      <OrderSummaryDialog
        open={isOrderSummaryDialogOpen}
        onOpenChange={setIsOrderSummaryDialogOpen}
        onSuccess={() => {
          onClear?.();
        }}
      />

      <AddToOrderDialog
        open={isAddToOrderDialogOpen}
        onOpenChange={setIsAddToOrderDialogOpen}
        cartItems={items}
        onSuccess={() => {
          clearCart();
          onClear?.();
        }}
      />
    </>
  );
}
